// Sprint F · Feature 3 — comments with @-mentions.
//
// Mention syntax follows the spec: `@[Display Name](user_id)` in
// stored text, rendered as a styled span. Composer has a popup
// triggered by typing "@" that lists team members and inserts the
// markup on click. The author's currentUser (TeamContext) is
// captured as both `user` (display) and `user_id`.
import React, { useState, useRef } from 'react';
import { useTeam, useCurrentUser, getTeamUser } from '../contexts/TeamContext';

const API = (import.meta.env?.VITE_API_URL || '') + '/api/v1/projects';

// Stored text: "Hi @[Erlend](u_erlend) can you check?"
// Visible text on screen: "Hi @Erlend can you check?"
const MENTION_RE = /@\[([^\]]+)\]\(([^)]+)\)/g;

// Parse mentions from stored markup → list of { name, userId }.
function extractMentions(text) {
  const out = [];
  let m;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text)) !== null) {
    out.push({ name: m[1], userId: m[2] });
  }
  return out;
}

// Render stored markup with styled mention chips.
function CommentText({ text }) {
  const parts = [];
  let lastIndex = 0;
  let m;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const name = m[1];
    const userId = m[2];
    const u = getTeamUser(userId);
    parts.push(
      <span key={`m-${m.index}`} className="comment-mention"
            style={u ? { color: u.color } : undefined}>
        @{name}
      </span>
    );
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

export default function CommentsPanel({ projectId, comments = [], section, onAdd }) {
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [mentionPicker, setMentionPicker] = useState(null); // { query, atPos } | null
  const taRef = useRef(null);
  const { team } = useTeam();
  const currentUser = useCurrentUser();

  const sectionComments = comments.filter(c =>
    !section || c.section === section || !c.section
  );

  const handleChange = (e) => {
    const val = e.target.value;
    setText(val);
    // Detect "@..." at the cursor → open picker with query.
    const cursor = e.target.selectionStart;
    const before = val.slice(0, cursor);
    const atIdx = before.lastIndexOf('@');
    if (atIdx !== -1) {
      const query = before.slice(atIdx + 1);
      // Only trigger if "@" is at start or preceded by whitespace, and query is short.
      const ok = (atIdx === 0 || /\s/.test(before[atIdx - 1])) && !/\s/.test(query);
      if (ok && query.length <= 30) {
        setMentionPicker({ query, atPos: atIdx });
        return;
      }
    }
    setMentionPicker(null);
  };

  const insertMention = (user) => {
    if (!mentionPicker) return;
    const { atPos } = mentionPicker;
    const cursor = taRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, atPos);
    const after = text.slice(cursor);
    const markup = `@[${user.name}](${user.id}) `;
    setText(before + markup + after);
    setMentionPicker(null);
    setTimeout(() => {
      const newPos = (before + markup).length;
      taRef.current?.focus();
      taRef.current?.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const mentions = extractMentions(text).map(m => m.userId);
      const resp = await fetch(`${API}/${projectId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text.trim(),
          section,
          user: currentUser?.name || 'Anonymous',
          user_id: currentUser?.id || null,
          mentioned_user_ids: [...new Set(mentions)],
        }),
      });
      if (resp.ok) {
        setText('');
        onAdd?.();
      }
    } catch (err) {
      console.error('Comment post failed:', err);
    }
    setPosting(false);
  };

  const handleResolve = async (commentId) => {
    try {
      await fetch(`${API}/${projectId}/comments/${commentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolved: true }),
      });
      onAdd?.();
    } catch (err) {
      console.error('Resolve failed:', err);
    }
  };

  // Filter team for the picker by query.
  const filteredTeam = mentionPicker
    ? team.filter(u =>
        u.name.toLowerCase().includes(mentionPicker.query.toLowerCase()) ||
        u.email.toLowerCase().includes(mentionPicker.query.toLowerCase())
      )
    : [];

  return (
    <div className="comments-panel">
      <h4 className="comments-title">
        Comments {section ? <span className="comments-section">/ {section}</span> : ''}
        {sectionComments.length > 0 && <span className="comments-count">{sectionComments.length}</span>}
      </h4>

      <div className="comments-list">
        {sectionComments.length === 0 && (
          <div className="comments-empty">No comments yet</div>
        )}
        {sectionComments.map(c => (
          <div key={c.id} className={`comment-item ${c.resolved ? 'comment-resolved' : ''}`}>
            <div className="comment-meta">
              <span className="comment-user">{c.user}</span>
              <span className="comment-time">{new Date(c.timestamp).toLocaleDateString()}</span>
              {!c.resolved && (
                <button className="comment-resolve-btn" onClick={() => handleResolve(c.id)}
                        title="Mark as resolved">Resolve</button>
              )}
            </div>
            <div className="comment-text"><CommentText text={c.text} /></div>
            {c.resolved && <span className="comment-badge-resolved">Resolved</span>}
          </div>
        ))}
      </div>

      <div className="comment-input" style={{ position: 'relative' }}>
        <textarea ref={taRef} value={text} onChange={handleChange}
                  placeholder={currentUser ? "Add a comment… type @ to tag someone" : "Sign in to add comments"}
                  rows={2}
                  disabled={!currentUser}
                  onKeyDown={e => {
                    if (e.key === 'Escape') setMentionPicker(null);
                    if (e.key === 'Enter' && e.metaKey) handleSubmit();
                  }} />
        {mentionPicker && filteredTeam.length > 0 && (
          <div className="mention-picker">
            {filteredTeam.map(u => (
              <button key={u.id} className="mention-picker-item"
                      onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}>
                <span className="mention-picker-avatar" style={{ background: u.color }}>{u.initials}</span>
                <span>{u.name}</span>
                <span className="mention-picker-email">{u.email}</span>
              </button>
            ))}
          </div>
        )}
        <button onClick={handleSubmit} disabled={!text.trim() || posting || !currentUser}>
          {posting ? 'Posting...' : 'Post'}
        </button>
      </div>
    </div>
  );
}
